// Phase 2.46.116 — Theme toggle (light / dark).  Persists in localStorage
// and applies `data-theme="light"|"dark"` on <html> so app-wide CSS
// variables can react.  Default = dark (matches the existing AMDiris
// visual language).  Available to every authenticated user from the
// top-right header (next to the notification bell).
import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

const STORAGE_KEY = "amdiris.theme";

function _getInitialTheme() {
  if (typeof window === "undefined") return "dark";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "light" || saved === "dark") return saved;
  // Phase 2.46.118 — honour OS / browser preference on first visit.
  // Only kicks in when the user has NOT explicitly toggled (saved is null).
  try {
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
      return "light";
    }
  } catch (_) { /* no-op */ }
  return "dark";
}

export function applyThemeToDocument(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.style.colorScheme = theme;
}

export function ThemeToggle() {
  const [theme, setTheme] = useState(_getInitialTheme);
  const [pulsing, setPulsing] = useState(false);

  // Apply theme to <html> on every change.  Persistence is handled by
  // `toggle()` so OS-driven changes don't lock in the user's saved value.
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  // Phase 2.46.119 — live OS-preference listener.  When the user has NOT
  // explicitly toggled (no localStorage entry), we follow the OS as it
  // changes mid-session.  An explicit toggle writes to localStorage and
  // permanently disables the listener for that user.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = (e) => {
      let saved = null;
      try { saved = window.localStorage.getItem(STORAGE_KEY); } catch (_) { /* no-op */ }
      if (saved === "light" || saved === "dark") return; // user override wins
      setTheme(e.matches ? "light" : "dark");
    };
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange); // Safari < 14
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);

  const toggle = () => {
    setPulsing(true);
    window.setTimeout(() => setPulsing(false), 500);
    setTheme(t => {
      const next = t === "dark" ? "light" : "dark";
      try { window.localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* no-op */ }
      return next;
    });
  };
  const isLight = theme === "light";
  return (
    <button onClick={toggle}
            data-testid="theme-toggle-btn"
            aria-label={`Switch to ${isLight ? "dark" : "light"} mode`}
            title={`Switch to ${isLight ? "dark" : "light"} mode`}
            className={`relative inline-flex items-center h-7 w-12 rounded-full border transition-all duration-300 ${pulsing ? "amdiris-theme-pulse" : ""}`}
            style={{
              background: isLight ? "rgba(0,180,216,0.18)" : "rgba(255,255,255,0.06)",
              borderColor: isLight ? "rgba(0,180,216,0.45)" : "rgba(255,255,255,0.10)",
            }}>
      <span aria-hidden="true"
            className="absolute flex items-center justify-center w-5 h-5 rounded-full transition-transform"
            style={{
              background: isLight ? "#FFFFFF" : "#0A1628",
              transform: isLight ? "translateX(26px)" : "translateX(2px)",
              boxShadow: isLight
                ? "0 1px 4px rgba(0,0,0,0.15)"
                : "0 0 0 1px rgba(255,255,255,0.10)",
            }}>
        {isLight ? <Sun size={11} color="#F59E0B" /> : <Moon size={11} color="#CBD5E1" />}
      </span>
    </button>
  );
}

// Tiny module-level bootstrap so the saved theme is applied BEFORE React
// mounts (avoids a flash of dark on a light-preferring user's reload).
applyThemeToDocument(_getInitialTheme());
